// Home.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import withWindowResize from '../resource/withWindowResize.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { getIsChinaPartner } from '../resource/partner.js';
import {
  HomeCategoryUi,
  SUPPORTED_FEED_QUERIES,
} from '../resource/feedConstants.js';

import PullToRefresh from '../container/PullToRefresh.js';
import UniversalContainer from '../component/UniversalContainer.jsx';
// import HomeJumboVideo from '../container/HomeJumboVideo.js';
import HomeLeaderboard from '../container/HomeLeaderboard.js';
import HomeShowcaseList from '../container/HomeShowcaseList.js';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import DiscoverFlixRow from '../container/DiscoverFlixRow.js';
import SeoHelmetWithTDK from '../component/SeoHelmetWithTDK.jsx';
import HomeJsonLd from '../component/HomeJsonLd.jsx';
import HomeLivestreamJsonLd from '../component/HomeLivestreamJsonLd.jsx';
import HomeVideoJsonLd from '../component/HomeVideoJsonLd.jsx';
import HomeLiveStreamFeed from '../container/HomeLiveStreamFeed.js';
import HomeSearchBar from '../container/HomeSearchBar.js';

import media from '../style/media.js';
import Heartbeat from '../style/Heartbeat.js';
import getIsInAutoPlayArea from '../resource/getIsInAutoPlayArea.js';
import LiveIcon from '../../img/ic_livestream_white.svg';
import { fromEvent, animationFrameScheduler, from } from 'rxjs';
import {
  debounceTime,
  pairwise,
  map,
  switchMap,
  reduce,
  filter,
  startWith,
} from 'rxjs/operators';

const tdkMap = {
  default: {
    title: 'seo-home-title',
    description: 'seo-home-description',
    h1: 'seo-home-h1',
    jsonLd: <HomeJsonLd />,
  },
  '/livestream': {
    title: 'seo_livestream_title',
    description: 'seo_livestream_description',
    h1: 'seo_livestream_h1',
    jsonLd: <HomeLivestreamJsonLd />,
  },
  '/video': {
    title: 'seo_video_title',
    description: 'seo_video_description',
    h1: 'seo_video_h1',
    jsonLd: <HomeVideoJsonLd />,
  },
};

const GAP_DEFAULT_SIZE = 16;
const BOTTOM_PADDING = 100; // Prevent content be overlapped by age verify banner.
const PSEUDO_TRENDING_HASHTAG = 'pseudo_trending_hashtag';

const Gap = styled.div`
  margin-bottom: ${({ marginBottom }) =>
    marginBottom || `${GAP_DEFAULT_SIZE}px`};
`;
const CategoryRowWrapper = styled(Gap)`
  position: relative;
`;
const isChinaPartner = getIsChinaPartner();

class Home extends React.PureComponent {
  state = {
    shouldRefresh: false,
  };
  flixRowList = [];

  constructor(props) {
    super(props);
    // fetch data for SSR
    const {
      staticContext,
      remoteConfigCategories,
      jumboVideoFeedName,
      trendingHashtagFeed,
      isFirstFeedFetched,
      configListError,
      fetchRemoteConfigList,
      fetchFeeds,
    } = props;
    if (staticContext.actions) {
      if (!remoteConfigCategories && !configListError) {
        staticContext.actions.push({
          function: fetchRemoteConfigList,
          argumentObject: {
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      } else if (remoteConfigCategories && !isFirstFeedFetched) {
        const feeds = remoteConfigCategories
          .split(' ')
          .filter(category => !category.startsWith('pseudo_'));
        if (jumboVideoFeedName) {
          // insert as first item to prevent interfere checking grid livestream feeds
          feeds.splice(0, 0, jumboVideoFeedName);
        }
        if (
          trendingHashtagFeed &&
          remoteConfigCategories.includes(PSEUDO_TRENDING_HASHTAG)
        ) {
          // insert as first item to prevent interfere checking grid livestream feeds
          feeds.splice(0, 0, trendingHashtagFeed);
        }
        staticContext.actions.push(
          ...feeds
            .filter(
              (feed, index) =>
                // we skip the fetching here, let component/HomeLiveStreamFeed do it instead.
                !(
                  /^livestream_|^user_/.test(feed) && index === feeds.length - 1
                )
            )
            // prevent SSR fetch too many feeds, and client download too many images before js load
            .slice(0, 5)
            .map((feed, index) => {
              const isShowcaseFeed = feed === jumboVideoFeedName;
              const result = {
                argumentObject: {
                  page: 1,
                  // showcase's limit should as same as container/HomeShowcaseList.js
                  limit: isShowcaseFeed ? 100 : undefined,
                  httpProxyHeaders: staticContext.httpProxyHeaders,
                },
              };
              const { category } = this.parseCategoryString(feed, index);
              result.function = fetchFeeds;
              result.argumentObject.type = category;
              return result;
            })
        );
      }
    }
  }

  getInCenterIndexObservable$ = deltaY =>
    from(this.flixRowList).pipe(
      reduce((acc, data, index) => {
        if (!data) return acc;
        return getIsInAutoPlayArea({ ref: data.ref }) ? acc.concat(index) : acc;
      }, []),
      map(inCenterIndex => ({ inCenterIndex, deltaY }))
    );

  getShouldPlayIndex = ({ deltaY, inCenterIndex }) => {
    if (inCenterIndex.length <= 0) return null;
    const shouldPlayIndex =
      deltaY >= 0 ? Math.max(...inCenterIndex) : Math.min(...inCenterIndex);
    return shouldPlayIndex ? this.flixRowList[shouldPlayIndex].feedId : null;
  };

  getWindowEventSubscrption = event => {
    return fromEvent(window, event)
      .pipe(
        startWith(null),
        debounceTime(200, animationFrameScheduler),
        map(() => window.pageYOffset),
        pairwise(),
        map(pair => pair[1] - pair[0]),
        filter(deltaY => Math.abs(deltaY) >= 10),
        switchMap(deltaY => this.getInCenterIndexObservable$(deltaY)),
        map(data => this.getShouldPlayIndex(data))
      )
      .subscribe(feedId => this.props.setShouldPlayFeedId({ feedId }));
  };

  componentDidMount() {
    this.scrollSubscrption = this.getWindowEventSubscrption('scroll');
    this.resizeSubscrption = this.getWindowEventSubscrption('resize');
  }

  componentWillUnmount() {
    this.scrollSubscrption?.unsubscribe();
    this.resizeSubscrption?.unsubscribe();
  }

  handleRefresh = async ({ reset }) => {
    this.setState({
      shouldRefresh: true,
    });
    setTimeout(() => {
      reset();
      this.setState({
        shouldRefresh: false,
      });
    });
  };

  getRefreshProps = () => {
    const { shouldRefresh } = this.state;
    return { shouldRefresh };
  };

  /**
   * @param {String} entry entry in config array string
   * @param {Number} index index of the entry
   */
  renderConfigRow = (entry, index) => {
    const { t } = this.props;

    if (entry.startsWith('pseudo_')) {
      if (['pseudo_banner', 'pseudo_video_banner'].includes(entry)) {
        return this.renderBanner(entry, index);
      }
      if (entry === PSEUDO_TRENDING_HASHTAG) {
        return (
          <CategoryRowWrapper key={entry}>
            <UniversalContainer
              {...this.getRefreshProps(entry)}
              category={entry}
              filename="HashtagCategoryList"
            />
          </CategoryRowWrapper>
        );
      }
      if (entry === 'pseudo_leaderboard') {
        return this.renderLeaderboard(entry, index);
      }
      if (entry === 'pseudo_showcase') {
        return (
          <main
            key={entry}
            css={`
              margin-bottom: 16px;
            `}
          >
            <HomeShowcaseList {...this.getRefreshProps(entry)} />
          </main>
        );
      }
      if (entry === 'pseudo_livestream_showcase') {
        return (
          <main key={entry}>
            {/* Livedtream Showcase */}
            <UniversalContainer
              filename="LivestreamShowcaseList"
              {...this.getRefreshProps(entry)}
            />
          </main>
        );
      }
      if (entry === 'pseudo_freezone_button' && !isChinaPartner) {
        return (
          <ButtonEntranceWrapper key={entry}>
            <FreezoneLink
              to="/freezone"
              onClick={() =>
                trackMixpanel({
                  type: EventTypes.USER_CLICKED,
                  payload: {
                    viewId: getViewId({ pathname: window.location.pathname }),
                    buttonId: ButtonId.All.ButtonTempFreezone,
                  },
                })
              }
            >
              {t('button_enter_freezone')}
            </FreezoneLink>
          </ButtonEntranceWrapper>
        );
      }

      if (entry === 'pseudo_livestream_button') {
        return (
          <ButtonEntranceWrapper key={entry}>
            <LivestreamLink
              to="/livestream"
              onClick={() =>
                trackMixpanel({
                  type: EventTypes.USER_CLICKED,
                  payload: {
                    viewId: getViewId({ pathname: window.location.pathname }),
                    buttonId: ButtonId.All.ButtonTempLivestream,
                  },
                })
              }
            >
              {t('button_enter_streaming')}
              <img src={LiveIcon} aria-hidden />
            </LivestreamLink>
          </ButtonEntranceWrapper>
        );
      }
      if (entry === 'pseudo_inbox') {
        return (
          <CategoryRowWrapper key={entry}>
            <UniversalContainer category={entry} filename="GroupedInboxRow" />
          </CategoryRowWrapper>
        );
      }
      return null;
    } else {
      return this.renderCategory(entry, index);
    }
  };

  renderLeaderboard = (key, index) => {
    const { leaderboardIds } = this.props;
    const region = leaderboardIds[0];
    return region ? (
      <LeaderboardWrapper key={`${index}-${key}`}>
        <WithIntersectionObserver threshold={0.5}>
          {({ isIntersecting }) => {
            return (
              <HomeLeaderboard
                {...this.getRefreshProps(key)}
                region={region}
                isIntersecting={isIntersecting}
              />
            );
          }}
        </WithIntersectionObserver>
      </LeaderboardWrapper>
    ) : null;
  };

  renderBanner = (key, index) => {
    return (
      <Gap key={`${index}-${key}`}>
        <UniversalContainer filename="HomeBanner" category={key} />
      </Gap>
    );
  };

  parseCategoryString = (category, index) => {
    const url = new URL(category, 'https://swag.live');
    let ui = url.searchParams.get('_ui');
    if (!ui) {
      const { remoteConfigCategories, livestreamFeedName } = this.props;
      const configRows = remoteConfigCategories?.split?.(' ').filter(a => a);
      if (index === configRows.length - 1) {
        if (/^(flix|posts)_/.test(category)) {
          ui = HomeCategoryUi.FLIX_GRID;
        } else if (/^livestream_|^user_/.test(category)) {
          ui = HomeCategoryUi.LIVESTREAM_GRID;
        } else {
          ui = HomeCategoryUi.STORY_GRID;
        }
      } else if (
        /^livestream_/.test(category) ||
        category === livestreamFeedName
      ) {
        ui = HomeCategoryUi.LIVESTREAM;
      } else if (/^(flix|posts)_/.test(category)) {
        ui = HomeCategoryUi.FLIX;
      } else if (/^stories_|^home$/.test(category)) {
        ui = HomeCategoryUi.STORY;
      } else if (/^shorts_/.test(category)) {
        ui = HomeCategoryUi.SHORTS;
      } else if (/^user/.test(category)) {
        ui = HomeCategoryUi.USER;
      } else {
        ui = HomeCategoryUi.STORY;
      }
    }
    [...url.searchParams.keys()].forEach(key => {
      if (!SUPPORTED_FEED_QUERIES.includes(key)) {
        url.searchParams.delete(key);
      }
    });
    return {
      category: decodeURIComponent(
        `${url.pathname.replace(/^\//, '')}${url.search}`
      ),
      ui,
    };
  };

  renderCategory = (originCategory, index) => {
    const { category, ui } = this.parseCategoryString(originCategory, index);
    switch (true) {
      case HomeCategoryUi.FLIX_GRID === ui: {
        return (
          <UniversalContainer
            category={category}
            filename="DiscoverFlixMoreRow"
            {...this.getRefreshProps(category)}
          />
        );
      }
      case HomeCategoryUi.FLIX === ui: {
        return (
          <div
            ref={el => {
              if (el) {
                this.flixRowList[index] = {
                  ref: el,
                  feedId: category,
                };
              }
            }}
          >
            <DiscoverFlixRow
              key={category}
              category={category}
              {...this.getRefreshProps(category)}
            />
          </div>
        );
      }
      case HomeCategoryUi.LIVESTREAM_GRID === ui: {
        return (
          <HomeLiveStreamFeed
            {...this.getRefreshProps(category)}
            category={category}
          />
        );
      }
      case HomeCategoryUi.LIVESTREAM === ui: {
        return (
          <UniversalContainer
            category={category}
            filename="HomeLiveStreamCategory"
            {...this.getRefreshProps(category)}
          />
        );
      }
      case HomeCategoryUi.USER === ui: {
        return (
          <UniversalContainer
            category={category}
            filename="UserCategory"
            {...this.getRefreshProps(category)}
            withHalo
          />
        );
      }
      case HomeCategoryUi.SHORTS === ui: {
        return (
          <UniversalContainer
            category={category}
            filename="ShortsRow"
            {...this.getRefreshProps(category)}
          />
        );
      }
      case HomeCategoryUi.STORY_GRID === ui: {
        return (
          <UniversalContainer
            category={category}
            filename="DiscoverMoreRow"
            {...this.getRefreshProps(category)}
          />
        );
      }
      case HomeCategoryUi.STORY === ui:
      default: {
        return (
          <UniversalContainer category={category} filename="FeedStoryRow" />
        );
      }
    }
  };

  render() {
    const {
      t,
      pathname,
      remoteConfigCategories,
      shouldRenderSearchBar,
      defaultOgImageUrl,
      defaultOgTitle,
      defaultOgDescription,
    } = this.props;
    const configRows = remoteConfigCategories?.split?.(' ').filter(a => a);
    const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();
    const shouldRenderSearchBarWrapper = /^\/(video[/]?)?$/.test(pathname);
    const tdk = tdkMap[pathname] || tdkMap.default;
    const rssFeeds = configRows
      .filter(c => !/^pseudo_/.test(c))
      .map(c => ({ feedId: c, title: t(c) }));
    return (
      <StyledHome isIphoneXSeriesPWA={isIphoneXSeriesPWA}>
        <SeoHelmetWithTDK
          title={t(defaultOgTitle || tdk.title)}
          description={t(defaultOgDescription || tdk.description)}
          feeds={rssFeeds}
          shouldAddSearchOnOgUrl={
            !!(defaultOgImageUrl || defaultOgTitle || defaultOgDescription)
          }
        />
        {tdk.jsonLd}
        <HeadingOne>{t(tdk.h1)}</HeadingOne>

        {shouldRenderSearchBarWrapper && (
          <SearchBarWrapper>
            {shouldRenderSearchBar && <HomeSearchBar />}
          </SearchBarWrapper>
        )}
        <PullToRefresh useWindow onRefresh={this.handleRefresh}>
          <>
            {/* Rows from config */}
            {configRows.map((homeCategoryId, index) => {
              return (
                <CategoryRowWrapper
                  // Avoid to use incorrect style on hydration
                  className={`categoryId_${homeCategoryId}`}
                  key={homeCategoryId}
                >
                  {this.renderConfigRow(homeCategoryId, index)}
                </CategoryRowWrapper>
              );
            })}
          </>
        </PullToRefresh>
      </StyledHome>
    );
  }
}

Home.propTypes = {
  staticContext: PropTypes.object,
  pathname: PropTypes.string,
  remoteConfigCategories: PropTypes.string,
  livestreamFeedName: PropTypes.string,
  jumboVideoFeedName: PropTypes.string,
  trendingHashtagFeed: PropTypes.string,
  defaultOgImageUrl: PropTypes.string,
  defaultOgTitle: PropTypes.string,
  defaultOgDescription: PropTypes.string,
  leaderboardIds: PropTypes.array,
  isFirstFeedFetched: PropTypes.bool,
  configListError: PropTypes.instanceOf(Error),
  setShouldPlayFeedId: PropTypes.func,
  fetchRemoteConfigList: PropTypes.func,
  fetchFeeds: PropTypes.func,
  t: PropTypes.func.isRequired,
  shouldRenderSearchBar: PropTypes.bool,
};

Home.defaultProps = {
  staticContext: {},
  pathname: '',
  remoteConfigCategories: '',
  livestreamFeedName: null,
  jumboVideoFeedName: null,
  trendingHashtagFeed: null,
  defaultOgImageUrl: '',
  defaultOgTitle: '',
  defaultOgDescription: '',
  leaderboardIds: [],
  isFirstFeedFetched: false,
  configListError: null,
  setShouldPlayFeedId: () => null,
  fetchRemoteConfigList: () => null,
  fetchFeeds: () => null,
  shouldRenderSearchBar: false,
};

const StyledHome = styled.div`
  position: relative;
  padding-top: ${headerHeight}px;
  padding-bottom: ${({ isIphoneXSeriesPWA }) =>
    footerNavigationHeight +
    (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0) +
    BOTTOM_PADDING}px;
  min-height: 100vh;
`;

const HeadingOne = styled.h1`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  font-size: 16px;
  color: #191919;
`;

const ButtonEntranceWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const EntranceLink = styled(Link)`
  min-width: 183px;
  height: 44px;
  border-radius: 5px;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  animation: 2s infinite ease-in-out ${Heartbeat()};
  img {
    padding-left: 4px;
  }
`;

const LivestreamLink = styled(EntranceLink)`
  padding: 0 16px;
  background-image: linear-gradient(105deg, #a445b3 4%, #d41872 54%, #ff0066);
`;

const FreezoneLink = styled(EntranceLink)`
  padding: 24px 14px;
  background-image: linear-gradient(286deg, #1761fb, #30e1f1);
`;

const LeaderboardWrapper = styled(Gap)``;

const SearchBarWrapper = styled(Gap)`
  padding: 0px 16px;
  & > div {
    display: none;
  }
  ${media.tablet`
    margin: 12px 0;
    & > div {
      display: block;
    }
  `};
  ${media.mobile`
    margin: 8px 0 12px;
    & > div {
      display: block;
    }
  `};
`;

export default withTranslation()(withWindowResize(Home));
